import katex from "katex";
import { toAuthenticatedFileUrl } from "@/app/lib/fileUrl";

/**
 * Preprocess markdown content to safely handle LaTeX math and currency dollar signs.
 *
 * Instead of relying on remark-math + rehype-katex pipeline (which can silently fail),
 * this function renders KaTeX directly to HTML and embeds the result.
 * ReactMarkdown must use `rehype-raw` to render the embedded HTML.
 *
 * Strategy: Detect bare LaTeX → Sanitize → Protect → Render → Escape
 * 0. Wrap bare LaTeX commands (without $) in $...$ delimiters
 * 1. Render $$...$$ block math directly with KaTeX
 * 2. Protect already-escaped \$ sequences
 * 3. Render $...$ inline math (only if content contains LaTeX-like patterns)
 * 4. Escape ALL remaining $ signs (currency, orphans, etc.)
 * 5. Restore protected sequences
 */

const KATEX_OPTIONS_INLINE: katex.KatexOptions = {
   displayMode: false,
   throwOnError: false,
   output: "html",
   strict: "ignore"
};

const KATEX_OPTIONS_BLOCK: katex.KatexOptions = {
   displayMode: true,
   throwOnError: false,
   output: "html",
   strict: "ignore"
};

/** Detect LaTeX-like patterns: \command, ^, _, {, }, [, ] */
const LATEX_PATTERN = /\\[a-zA-Z]|[\^_{}\[\]]/;

/**
 * Sanitize LaTeX formula before passing to KaTeX.
 *
 * KaTeX treats `%` as a comment character (like standard LaTeX).
 * LLMs often output `\text{%}` but KaTeX needs `\text{\%}`.
 * This function escapes unescaped `%` inside \text{...} blocks.
 */
function sanitizeLatexFormula(formula: string): string {
   // Escape unescaped % inside \text{...}, \textbf{...}, etc.
   return formula.replace(
      /(\\text(?:bf|it|tt|rm|sf|normal)?)\{([^}]*)\}/g,
      (match, command: string, inner: string) => {
         // Escape % that isn't already escaped
         const sanitizedInner = inner.replace(/(?<!\\)%/g, "\\%");
         return `${command}{${sanitizedInner}}`;
      }
   );
}

/**
 * Step 0: Detect bare LaTeX commands that LLMs output WITHOUT $...$ wrapping.
 * Wraps them in $...$ so the main pipeline can render them with KaTeX.
 *
 * Handles:
 * - Commands with one arg:  \text{%}, \sqrt{x}, \mathbf{v}
 * - Commands with two args: \frac{a}{b}
 * - Bare commands:          \times, \div, \alpha, \%, \infty
 *
 * The negative lookbehind (?<!\$) prevents double-wrapping for content already inside $...$
 */
function wrapBareLatexCommands(content: string): string {
   let result = content;

   // Wrap LaTeX commands with arguments: \text{...}, \frac{...}{...}, etc.
   result = result.replace(
      /(?<!\$)(\\(?:text|textbf|textit|texttt|textrm|textsf|mathbf|mathrm|mathit|mathsf|mathtt|mathcal|mathbb|frac|dfrac|tfrac|sqrt|vec|hat|bar|tilde|dot|ddot|overline|underline|overbrace|underbrace|boxed)\{[^}]*\}(?:\{[^}]*\})?)/g,
      (_, latex: string) => `$${latex}$`
   );

   // Wrap bare single LaTeX commands (no args): \times, \alpha, etc.
   // Negative lookahead (?![a-zA-Z{]) prevents partial matches like \text → \t
   result = result.replace(
      /(?<!\$)(\\(?:times|div|pm|mp|cdot|cdots|ldots|dots|infty|approx|neq|leq|geq|sim|propto|sum|prod|int|partial|nabla|forall|exists|cap|cup|to|rightarrow|leftarrow|Rightarrow|Leftarrow|alpha|beta|gamma|delta|epsilon|varepsilon|zeta|eta|theta|iota|kappa|lambda|mu|nu|xi|pi|rho|sigma|tau|upsilon|phi|varphi|chi|psi|omega|Gamma|Delta|Theta|Lambda|Xi|Pi|Sigma|Phi|Psi|Omega))(?![a-zA-Z{])/g,
      (_, latex: string) => `$${latex}$`
   );

   return result;
}

function cleanMalformedLatex(content: string): string {
   // Fix LLM hallucinations where stray unescaped `$` are placed inside `\frac` arguments.
   // For example: \frac{...$}{...$} -- this breaks KaTeX parsing.
   // Unescaped `$` inside `\frac` is always invalid in KaTeX, so it's safe to remove them.
   let result = "";
   let i = 0;
   while (i < content.length) {
      if (content.startsWith("\\frac{", i)) {
         const fracStart = i;
         i += 6; // skip \frac{
         let depth = 1;
         let numeratorEnd = -1;
         for (let j = i; j < content.length; j++) {
            if (content[j] === "{") depth++;
            else if (content[j] === "}") depth--;
            if (depth === 0) {
               numeratorEnd = j;
               break;
            }
         }
         if (numeratorEnd !== -1 && content[numeratorEnd + 1] === "{") {
            const denominatorStart = numeratorEnd + 2;
            depth = 1;
            let denominatorEnd = -1;
            for (let j = denominatorStart; j < content.length; j++) {
               if (content[j] === "{") depth++;
               else if (content[j] === "}") depth--;
               if (depth === 0) {
                  denominatorEnd = j;
                  break;
               }
            }
            if (denominatorEnd !== -1) {
               let numerator = content.substring(fracStart + 6, numeratorEnd);
               let denominator = content.substring(denominatorStart, denominatorEnd);
               numerator = numerator.replace(/(?<!\\)\$/g, "");
               denominator = denominator.replace(/(?<!\\)\$/g, "");
               result += "\\frac{" + numerator + "}{" + denominator + "}";
               i = denominatorEnd + 1;
               continue;
            }
         }
         result += "\\frac{";
      } else {
         result += content[i];
         i++;
      }
   }
   return result;
}

/**
 * Normalize common LLM markdown glitches before parsing.
 *
 * Handles:
 * - Escaped newlines from JSON/stringified payloads: \n -> actual newline
 * - Pseudo image tokens like [Image: https://...]
 * - Near-markdown image variants emitted by LLMs
 * - Raw HTML img tags
 */
const IMAGE_URL_PATTERN =
   /https?:\/\/[^\s<>"')\]]+(?:\.(?:png|jpe?g|gif|webp|svg)(?:\?[^\s<>"')\]]*)?|\/(?:image|images|img|media|uploads|ckeditor-files)\/?[^\s<>"')\]]*)/i;

const ABSOLUTE_STORED_FILE_URL_PATTERN =
   /https?:\/\/[^\s<>"')\]]+\/+(?:uploads|files)\/[^\s<>"')\]]+/gi;

function stripTrailingPunctuation(value: string): string {
   return value.replace(/[.,;:!?]+$/g, "");
}

function isImageUrl(url: string): boolean {
   return IMAGE_URL_PATTERN.test(stripTrailingPunctuation(url.trim()));
}

function toMarkdownImage(url: string, alt = "Image"): string {
   return `![${alt}](${stripTrailingPunctuation(url.trim())})`;
}

function replacePseudoImageToken(
   match: string,
   label: string | undefined,
   url: string
): string {
   if (!isImageUrl(url)) {
      return match;
   }

   const normalizedAlt = label?.trim() || "Image";
   return `\n\n${toMarkdownImage(url, normalizedAlt)}\n\n`;
}

function normalizeImageMarkdown(content: string): string {
   return content
      .replace(
         /<img\b[^>]*src=["']([^"']+)["'][^>]*alt=["']([^"']*)["'][^>]*>/gi,
         (_match, src: string, alt: string) =>
            isImageUrl(src) ? toMarkdownImage(src, alt.trim() || "Image") : _match
      )
      .replace(
         /<img\b[^>]*alt=["']([^"']*)["'][^>]*src=["']([^"']+)["'][^>]*>/gi,
         (_match, alt: string, src: string) =>
            isImageUrl(src) ? toMarkdownImage(src, alt.trim() || "Image") : _match
      )
      .replace(
         /\[\s*([a-zA-Z ]{2,20})\s*:\s*<a\b[^>]*href=["']([^"']+)["'][^>]*>[\s\S]*?<\/a>\s*\]/gi,
         (match, label: string, url: string) => replacePseudoImageToken(match, label, url)
      )
      .replace(
         /\[\s*([a-zA-Z ]{2,20})\s*:\s*(https?:\/\/[^\s\]]+)\s*\]/gi,
         (match, label: string, url: string) => replacePseudoImageToken(match, label, url)
      )
      .replace(/\[\s*(https?:\/\/[^\s\]]+)\s*\]/gi, (match, url: string) =>
         replacePseudoImageToken(match, "Image", url)
      )
      .replace(
         /(^|[\s(>])([a-zA-Z ]{2,20})\s*:\s*<a\b[^>]*href=["']([^"']+)["'][^>]*>[\s\S]*?<\/a>/gim,
         (match, prefix: string, label: string, url: string) =>
            isImageUrl(url)
               ? `${prefix}\n\n${toMarkdownImage(url, label.trim())}\n\n`
               : match
      )
      .replace(
         /(^|[\s(>])([a-zA-Z ]{2,20})\s*:\s*(https?:\/\/\S+)/gim,
         (match, prefix: string, label: string, url: string) =>
            isImageUrl(url)
               ? `${prefix}\n\n${toMarkdownImage(url, label.trim())}\n\n`
               : match
      )
      .replace(/!\[\s*\]\((https?:\/\/[^)\s]+)\)/gi, (_match, url: string) =>
         isImageUrl(url) ? toMarkdownImage(url) : _match
      )
      .replace(
         /(?<!!)\[(image|img|photo|picture|figure|illustration)\]\((https?:\/\/[^)\s]+)\)/gi,
         (_match, alt: string, url: string) =>
            isImageUrl(url) ? toMarkdownImage(url, alt.trim()) : _match
      )
      .replace(
         /\[!\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)\]\((https?:\/\/[^)\s]+)\)/gi,
         (_match, alt: string, imageUrl: string) =>
            isImageUrl(imageUrl)
               ? toMarkdownImage(imageUrl, alt.trim() || "Image")
               : _match
      );
}

function normalizeGeneratedMarkdown(content: string): string {
   const authenticatedFileUrls = content.replace(
      ABSOLUTE_STORED_FILE_URL_PATTERN,
      toAuthenticatedFileUrl
   );
   const normalizedNewlines = authenticatedFileUrls.replace(/\\n/g, "\n");
   return normalizeImageMarkdown(normalizedNewlines);
}

/**
 * Strip LLM thinking/reasoning tags that are not valid HTML
 * Some models (DeepSeek, Gemma, etc.) emit <thought>, <thinking>, <reflection> tags
 * which cause React warnings when rehype-raw passes them through as unknown elements
 */
export function stripReasoningTags(content: string): string {
   // Phase 1: Strip complete blocks <tag>...content...</tag> (requires matching open+close pair)
   let result = content.replace(
      /<(thought|thinking|reflection|reasoning)(?:\s[^>]*)?>[\s\S]*?<\/\1>/gi,
      ""
   );
   // Phase 2: Strip any remaining orphaned opening/closing tags (e.g. during streaming)
   result = result.replace(
      /<\/?(thought|thinking|reflection|reasoning)(?:\s[^>]*)?>/gi,
      ""
   );
   return result;
}

export function preprocessMathContent(content: string): string {
   const placeholders: string[] = [];
   const placeholderNonce = Math.random().toString(36).slice(2, 10);

   const buildPlaceholderSpan = (index: number): string =>
      `<span data-math-placeholder="${placeholderNonce}:${index}"></span>`;
   const buildProtectSpan = (index: number): string =>
      `<span data-math-protect="${placeholderNonce}:${index}"></span>`;

   const protect = (html: string): string => {
      const index = placeholders.length;
      placeholders.push(html);
      // Add a per-call nonce so user-provided HTML cannot collide with restore markers.
      return buildPlaceholderSpan(index);
   };

   const renderKatex = (formula: string, displayMode: boolean): string => {
      // Sanitize the formula before rendering (escape % in \text{}, etc.)
      const sanitized = sanitizeLatexFormula(formula);
      try {
         return katex.renderToString(
            sanitized.trim(),
            displayMode ? KATEX_OPTIONS_BLOCK : KATEX_OPTIONS_INLINE
         );
      } catch {
         // If KaTeX still fails, return the original formula as plain text
         return `<span class="katex-error" style="color:inherit">${formula}</span>`;
      }
   };

   // Step -2: Strip LLM thinking/reasoning tags that are not valid HTML
   // Some models (DeepSeek, Gemma, etc.) emit <thought>, <thinking>, <reflection> tags
   // which cause React warnings when rehype-raw passes them through as unknown elements
   let result = normalizeGeneratedMarkdown(stripReasoningTags(content));

   // Step -1: Clean hallucinated unescaped $ inside structure like \frac
   result = cleanMalformedLatex(result);

   // Step 0: Wrap bare LaTeX commands in $...$ for processing
   // IMPORTANT: We must protect existing math blocks first, so wrapBareLatexCommands
   // doesn't inject $ inside them (e.g. finding \text{} inside a \frac{} and turning it into $\text{}$)
   const existingMath: string[] = [];
   const protectMath = (text: string): string => {
      const idx = existingMath.length;
      existingMath.push(text);
      return buildProtectSpan(idx);
   };

   // Protect existing $$...$$ and $...$ blocks, and escaped \$
   result = result.replace(/\$\$([\s\S]+?)\$\$/g, protectMath);
   result = result.replace(/\\\$/g, protectMath);
   result = result.replace(/\$([^$\n]+?)\$/g, protectMath);

   // Protect UI injection placeholders like [[CHART:BTC]] so they are untouched
   // by wrapBareLatexCommands and the rest of the pipeline. We use `protect`
   // instead of `protectMath` because `protect` restores at the VERY END.
   result = result.replace(/\[\[[A-Z0-9_-]+:[^\]]+\]\]/gi, (match) => protect(match));

   // Wrap bare latex commands ONLY in text that is not already inside math blocks
   result = wrapBareLatexCommands(result);

   // Restore existing math blocks
   for (let i = existingMath.length - 1; i >= 0; i--) {
      result = result.replace(buildProtectSpan(i), existingMath[i]);
   }

   // Step 1: Render $$...$$ block math directly with KaTeX → HTML
   result = result.replace(/\$\$([\s\S]+?)\$\$/g, (_, formula: string) => {
      if (formula.includes("<span data-math-placeholder=")) {
         const safeFormula = formula.replace(
            new RegExp(
               `<span data-math-placeholder="${placeholderNonce}:(\\d+)"><\\/span>`,
               "g"
            ),
            "\\text{@@$1@@}"
         );
         let rendered = renderKatex(safeFormula, true);
         rendered = rendered.replace(/@@(\d+)@@/g, (_, index: string) =>
            buildPlaceholderSpan(Number(index))
         );
         return protect(rendered);
      }
      return protect(renderKatex(formula, true));
   });

   // Step 2: Protect already-escaped \$ (prevent double-escaping)
   result = result.replace(/\\\$/g, (match) => protect(match));

   // Step 3: Find $...$ pairs — render as math if LaTeX-like, else leave for escaping
   result = result.replace(/\$([^$\n]+?)\$/g, (match, inner: string) => {
      const trimmed = inner.trim();
      const normalizedTrimmed = trimmed.replace(/\\%/g, "%");

      // Fix LLM hallucinations for percentage ranges (e.g. $50% - 70%$ or $60%$)
      // Drop BOTH $ signs if it strictly matches a percentage or a range ending in %
      if (
         /^[\d.,]+%?(?:\s*-\s*[\d.,]+%)?$/.test(normalizedTrimmed) &&
         normalizedTrimmed.includes("%")
      ) {
         return normalizedTrimmed;
      }

      // Fix LLM hallucinations for simple fractions (e.g. $1/3$, $1/4$)
      // Drop BOTH $ signs
      if (/^[\d.,]+\/[\d.,]+(?:\s*-\s*[\d.,]+\/[\d.,]+)?$/.test(normalizedTrimmed)) {
         return normalizedTrimmed;
      }

      // Fix LLM hallucinations for currency/number ranges (e.g. $126k$, $100 - 200k$)
      // Keep the first $ and drop the trailing $
      if (/^[\d.,]+[kKmMbBtT]?(?:\s*-\s*[\d.,]+[kKmMbBtT]?)?$/.test(trimmed)) {
         return `$${trimmed}`;
      }

      if (LATEX_PATTERN.test(inner)) {
         if (inner.includes("<span data-math-placeholder=")) {
            const safeInner = inner.replace(
               new RegExp(
                  `<span data-math-placeholder="${placeholderNonce}:(\\d+)"><\\/span>`,
                  "g"
               ),
               "\\text{@@$1@@}"
            );
            let rendered = renderKatex(safeInner, false);
            rendered = rendered.replace(/@@(\d+)@@/g, (_, index: string) =>
               buildPlaceholderSpan(Number(index))
            );
            return protect(rendered);
         }
         return protect(renderKatex(inner, false));
      }
      return match;
   });

   // Step 4: Escape ALL remaining $ signs (currency, orphans, etc.)
   result = result.replace(/\$/g, "\\$");

   // Step 5: Restore protected blocks (reverse order to avoid substring collisions)
   for (let i = placeholders.length - 1; i >= 0; i--) {
      result = result.replace(buildPlaceholderSpan(i), placeholders[i]);
   }

   return result;
}
